import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useInterval } from 'react-use'
import styles from './index.module.sass'

dayjs.extend(duration)

function numberPadding (num: number) {
  const digits = `${num}`.split('')
  // add zero to single digits
  if (digits.length < 2) digits.unshift('0')
  return digits
}

class Countdown {
  endTime: Dayjs
  time: string[]
  prevTime: string[]

  constructor () {
    this.time = []
    this.endTime = dayjs((2 ** 31 - 1) * 1000)
    this.update()
  }

  setEndTime (endTime: Date | number) {
    if (!endTime) this.endTime = dayjs()
    else this.endTime = dayjs(endTime)
  }

  getTimeLeft () {
    const now = dayjs()
    const diff = dayjs.duration(this.endTime.diff(now))

    if (diff.asSeconds() >= 0) {
      const displayDigits = []
      const hours = Math.min(Math.floor(diff.asHours()), 99)
      displayDigits.push(...numberPadding(hours))
      displayDigits.push(...numberPadding(diff.minutes()))
      displayDigits.push(...numberPadding(diff.seconds()))
      return displayDigits
    } else {
      return ['-', '-', '-', '-', '-', '-']
    }
  }

  update () {
    // start with all dashes
    if (!this.time.length) {
      let digitCount = 6

      while (digitCount--) this.time.push('-')
    }
    // update data
    const displayDigits = this.getTimeLeft()

    // update display
    this.prevTime = this.time
    this.time = displayDigits
  }
}

const instance = new Countdown()

interface Props {
  endTime?: Date | number;
}

const CountdownTimer: FC<Props> = ({ endTime }) => {
  const countdown = useMemo(() => instance, [])
  const [digits, setDigits] = useState(Array(6).map(() => ''))
  const [prevDigits, setPrevDigits] = useState(Array(6).map(() => ''))
  const [doAnimations, setDoAnimations] = useState(Array(6).map(() => false))

  const update = useCallback(() => {
    countdown.update()
    setDigits(countdown.time)
    setPrevDigits(countdown.prevTime)
    const doActions = countdown.time.map((t, i) => t !== countdown.prevTime[i])
    setDoAnimations(doActions)
  }, [countdown])

  useEffect(() => {
    countdown.setEndTime(endTime)
  }, [countdown, endTime])

  useEffect(() => {
    update()
  }, [update])

  useInterval(update, 1000)

  return (
    <div className={styles.cd}>
      <div className={styles.cdUnitGroup}>
        <div className={styles.cdUnit}>
          <div className={styles.cdDigits}>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[0] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[0] ? styles.cdNextDigitFade : ''}
                >
                  {digits[0]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[0] ? styles.cdPrevDigitFade : ''}
                >
                  {prevDigits[0]}
                </div>
              </div>
            </div>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[1] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[1] ? styles.cdNextDigitFade : ''}
                >
                  {digits[1]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[1] ? styles.cdPrevDigitFade : ''}
                >
                  {prevDigits[1]}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.cdUnitDivider}>:</div>
        <div className={styles.cdUnit}>
          <div className={styles.cdDigits}>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[2] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[2] ? styles.cdNextDigitFade : ''}
                >
                  {digits[2]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[2] ? styles.cdPrevDigitFade : ''}
                >
                  {prevDigits[2]}
                </div>
              </div>
            </div>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[3] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[3] ? styles.cdNextDigitFade : ''}
                >
                  {digits[3]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[3] ? styles.cdPrevDigitFade : ''}
                >
                  {prevDigits[3]}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.cdUnitDivider}>:</div>
        <div className={styles.cdUnit}>
          <div className={styles.cdDigits}>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[4] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[4] ? styles.cdNextDigitFade : ''}
                >
                  {digits[4]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[4] ? styles.cdPrevDigitFade : ''}
                >
                  {prevDigits[4]}
                </div>
              </div>
            </div>
            <div className={styles.cdDigitBg}>
              <div
                className={clsx(
                  styles.cdDigit,
                  doAnimations[5] ? styles.cdDigitRollIn : ''
                )}
              >
                <div
                  data-pos="next"
                  className={doAnimations[5] ? styles.cdNextDigitFade : ''}
                >
                  {digits[5]}
                </div>
                <div className={styles.cdDigitGap}></div>
                <div
                  data-pos="prev"
                  className={doAnimations[5] ? styles.cdPrevDigitFade : ''}
                  onAnimationEnd={() =>
                    setDoAnimations(Array(6).map(() => false))
                  }
                >
                  {prevDigits[5]}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CountdownTimer
